/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { FocusTrap } from 'focus-trap-react';
import * as React from 'react';
import ReactDom from 'react-dom';

import { PopOverPrimitive } from './pop-over.primitive';
import type { PopOverProps } from './pop-over.types';
import { autoPositionByRef } from './utils/auto-position-item';

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

const EVENT_LISTENER_OPTIONS =
  parseInt(React.version.split('.')[0]) >= 17 ? { capture: true } : {};

type PopOverRef = HTMLDivElement & {
  reposition: () => void;
};

export const PopOver = React.forwardRef<HTMLDivElement, PopOverProps>(
  (
    {
      alwaysUp = false,
      children,
      className,
      focusTrapProps,
      isInFlow = false,
      isStatic,
      offset,
      onOutsideClick,
      show = false,
      showCloseButton,
      triggerRef,
      ...props
    },
    forwardedRef
  ): JSX.Element | null => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [position, setPosition] = React.useState({});

    const reposition = React.useCallback(() => {
      if (show && !isStatic) {
        const newPosition = autoPositionByRef(
          triggerRef,
          containerRef,
          isInFlow,
          offset,
          alwaysUp
        );
        setPosition(newPosition);
      }
    }, [alwaysUp, isInFlow, isStatic, offset, show, triggerRef]);

    useIsomorphicLayoutEffect(() => {
      reposition();
    }, [triggerRef, containerRef, show, reposition]);

    React.useImperativeHandle(forwardedRef, () => {
      if (!containerRef.current) {
        return null as unknown as PopOverRef;
      }

      return {
        ...containerRef.current,
        reposition,
      } as PopOverRef;
    }, [reposition]);

    // Focus management
    React.useEffect(() => {
      if (focusTrapProps?.active === false) return;

      if (show) {
        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.focus();
          }
        }, 50);
      }
    }, [show, focusTrapProps?.active]);

    // Keyboard handling
    React.useEffect(() => {
      const handleKey = (event: KeyboardEvent): void => {
        if (show && onOutsideClick && event.key === 'Escape') {
          event.preventDefault();
          onOutsideClick();
        }
      };

      document.addEventListener('keydown', handleKey);
      return () => {
        document.removeEventListener('keydown', handleKey);
      };
    }, [onOutsideClick, show]);

    React.useEffect(() => {
      const checkOutSideClick = (e: MouseEvent): void => {
        if (
          containerRef?.current?.contains(e.target as Node) ||
          triggerRef?.current?.contains(e.target as Node)
        ) {
          return;
        }

        if (onOutsideClick) onOutsideClick();
      };

      if (show) {
        window.addEventListener('resize', reposition, EVENT_LISTENER_OPTIONS);
        document.addEventListener(
          'click',
          checkOutSideClick,
          EVENT_LISTENER_OPTIONS
        );
      }

      return () => {
        window.removeEventListener(
          'resize',
          reposition,
          EVENT_LISTENER_OPTIONS
        );
        document.removeEventListener(
          'click',
          checkOutSideClick,
          EVENT_LISTENER_OPTIONS
        );
      };
    }, [onOutsideClick, reposition, show, triggerRef]);

    const commonProps = {
      className,
      containerRef,
      onClose: onOutsideClick,
      position,
      role: 'dialog',
      showCloseButton,
    };

    const { focusTrapOptions: userFocusTrapOptions, ...otherFocusTrapProps } =
      focusTrapProps || {};
    const mergedFocusTrapOptions = {
      escapeDeactivates: Boolean(onOutsideClick),
      fallbackFocus: () => containerRef.current || document.body,
      returnFocusOnDeactivate: true,
      ...(userFocusTrapOptions || {}),
    };

    if (!show) {
      return null;
    }

    if (isInFlow) {
      return (
        <FocusTrap
          active={show}
          {...otherFocusTrapProps}
          focusTrapOptions={mergedFocusTrapOptions}
        >
          <PopOverPrimitive {...commonProps} {...props}>
            {children}
          </PopOverPrimitive>
        </FocusTrap>
      );
    }

    return ReactDom.createPortal(
      <FocusTrap
        active={show}
        {...otherFocusTrapProps}
        focusTrapOptions={mergedFocusTrapOptions}
      >
        <PopOverPrimitive {...commonProps} {...props}>
          {children}
        </PopOverPrimitive>
      </FocusTrap>,
      document.body
    );
  }
);

PopOver.displayName = 'PopOver';
